import { useCallback, useEffect, useRef, useState } from 'react'
import cn from 'classnames'

import { DropdownInput } from './dropdown-input'
import { DropdownList } from './dropdown-list'
import { useKeyboard } from './use-keyboard'
import { useSearch } from './use-search'
import { BaseItem, PlainSearchDropdownProps } from './types'
import { PlainDropDownSurfaceCommon, SurfaceType } from '../plain-dropdown-surface-common'
import { defaultRenderItem } from './default-render-item'

import styles from './plain-search-dropdown.module.scss'

export function PlainSearchDropdown<T extends BaseItem>({
  id,
  value,
  defaultValue,
  onChange,
  loadItems,
  placeholder,
  disabled,
  pageSize = 20,
  debounce = 300,
  className,
  dropdownClassName,
  inputClassName,
  itemClassName,
  itemHeight = 30,
  emptyComponent,
  loadingComponent,
  error: errorHighlighted,
  errorComponent,
  renderItem = defaultRenderItem,
}: PlainSearchDropdownProps<T>) {
  //
  // Controlled / uncontrolled
  //

  const controlled = value !== undefined

  const [internalValue, setInternalValue] = useState<T>()

  const selectedItem = controlled ? value : internalValue || defaultValue

  const [inputValue, setInputValue] = useState(selectedItem?.title ?? '')

  //
  // Search
  //

  const { items, loading, error, hasMore, searchDebounced, loadMore, reload, reset } = useSearch({
    loadItems,
    pageSize,
    debounce,
  })

  //
  // Keyboard
  //

  const handleReset = useCallback(() => {
    reset()
    setInputValue(selectedItem?.title ?? '')
  }, [reset, selectedItem])

  const keyboard = useKeyboard({
    itemCount: items.length,
    onReset: handleReset,
  })

  //
  // Handlers
  //

  function handleSelect(item: T) {
    if (!controlled) {
      setInternalValue(item)
    }
    onChange?.(item)
    setInputValue(item.title)
    reset()
  }

  const handleClear = useCallback(() => {
    if (!controlled) {
      setInternalValue(undefined)
    }
    onChange?.(undefined)
    setInputValue('')
    reset()
  }, [controlled, onChange, reset])

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const text = event.target.value
      setInputValue(text)
      searchDebounced(text)
    },
    [searchDebounced],
  )

  //
  // Effects
  //

  useEffect(() => {
    if (!controlled) {
      return
    }
    setInputValue(value?.title ?? '')
  }, [controlled, value])

  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <>
      <PlainDropDownSurfaceCommon
        id={id}
        type={SurfaceType.Listbox}
        className={className}
        surfaceContentClassName={cn(styles.root, dropdownClassName)}
        onOpenState={(opened) => {
          if (opened) {
            reload(inputValue)
          } else {
            handleReset()
          }
        }}
        surfaceContent={(toggle) => (
          <DropdownList
            id={id}
            items={items}
            activeIndex={keyboard.activeIndex}
            selectedItem={selectedItem}
            loading={loading}
            error={error}
            emptyComponent={emptyComponent}
            loadingComponent={loadingComponent}
            errorComponent={errorComponent}
            renderItem={renderItem}
            itemClassName={itemClassName}
            itemHeight={itemHeight}
            onItemHover={keyboard.setActiveIndex}
            onItemClick={(item) => {
              handleSelect(item)
              toggle()
              requestAnimationFrame(() => {
                inputRef.current?.focus()
              })
            }}
            onScroll={(event) => {
              const target = event.currentTarget
              if (
                hasMore &&
                !loading &&
                target.scrollHeight - target.scrollTop - target.clientHeight < 100
              ) {
                loadMore()
              }
            }}
          />
        )}
      >
        {(toggle, opened, areaProps) => (
          <DropdownInput
            ref={inputRef}
            value={inputValue}
            placeholder={placeholder}
            disabled={disabled}
            opened={opened}
            className={inputClassName}
            error={errorHighlighted || !!error}
            onClear={
              selectedItem
                ? () => {
                    handleClear()
                    if (opened) toggle()
                  }
                : undefined
            }
            onToggle={() => {
              toggle()
              requestAnimationFrame(() => {
                inputRef.current?.focus()
              })
            }}
            onFocus={() => {
              // if (!opened) {
              //   toggle()
              // }
            }}
            onBlur={() => {
              //
              // Закрытие выполняет PlainDropDownSurface
              //
            }}
            onChange={(event) => {
              handleInputChange(event)
              if (!opened) {
                toggle()
              }
            }}
            onKeyDown={(event) => {
              if (!opened) {
                if (event.key === 'Backspace' && inputValue === '' && selectedItem) {
                  event.preventDefault()
                  handleClear()
                  return
                }

                switch (event.key) {
                  case 'ArrowDown':
                  case 'ArrowUp':
                    toggle()
                    return
                }
                return
              }

              if (opened && event.key === 'Enter') {
                event.preventDefault()
                const item = items[keyboard.activeIndex]
                if (item) {
                  handleSelect(item)
                  toggle()
                  requestAnimationFrame(() => {
                    inputRef.current?.focus()
                  })
                }
                return
              }

              keyboard.onKeyDown(event)
            }}
            activeDescendant={
              keyboard.activeIndex >= 0 ? `${id}-item-${keyboard.activeIndex}` : undefined
            }
            {...areaProps}
          />
        )}
      </PlainDropDownSurfaceCommon>
      <input name={id} value={selectedItem?.id} type="hidden" />
    </>
  )
}
